import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  private focusableElements: HTMLElement[];
  private firstFocusableElement: HTMLElement;
  private lastFocusableElement: HTMLElement;
  private previouslyFocusedElement: HTMLElement;

  connect(): void {
    this.updateFocusableElements();

    this.previouslyFocusedElement = document.activeElement as HTMLElement;

    this.element.addEventListener("keydown", this.handleKeydown.bind(this));

    this.focusFirstElement();
  }

  disconnect(): void {
    this.element.removeEventListener("keydown", this.handleKeydown.bind(this));

    // Return focus to previously focused element
    if (
      this.previouslyFocusedElement &&
      document.contains(this.previouslyFocusedElement)
    ) {
      this.previouslyFocusedElement.focus();
    }
  }

  private updateFocusableElements(): void {
    const focusableSelectors = [
      "button:not([disabled])",
      "input:not([disabled])",
      "select:not([disabled])",
      "textarea:not([disabled])",
      "a[href]",
      '[tabindex]:not([tabindex="-1"])',
    ].join(", ");

    this.focusableElements = Array.from(
      this.element.querySelectorAll(focusableSelectors)
    ) as HTMLElement[];

    this.firstFocusableElement = this.focusableElements[0] || null;
    this.lastFocusableElement =
      this.focusableElements[this.focusableElements.length - 1] || null;
  }

  private handleKeydown(event: KeyboardEvent): void {
    // Only handle Tab key
    if (event.key !== "Tab") {
      return;
    }

    // If no focusable elements, prevent default and return
    if (this.focusableElements.length === 0) {
      event.preventDefault();
      return;
    }

    // If only one focusable element, keep focus on it
    if (this.focusableElements.length === 1) {
      event.preventDefault();
      this.firstFocusableElement?.focus();
      return;
    }

    if (event.key === "Tab") {
      if (event.shiftKey) {
        // Shift + Tab
        if (document.activeElement === this.firstFocusableElement) {
          event.preventDefault();
          this.lastFocusableElement.focus();
        }
      } else {
        // Tab
        if (document.activeElement === this.lastFocusableElement) {
          event.preventDefault();
          this.firstFocusableElement.focus();
        }
      }
    }
  }

  private focusFirstElement(): void {
    if (this.firstFocusableElement) {
      this.firstFocusableElement.focus();
    }
  }
}
